import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import c from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Button from '@wix/da-shared-react/pkg/Button';
import isValidEmailAddress from '@wix/da-shared-react/pkg/utils/isValidEmailAddress';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SisuPage, {
  SisuPageHeader,
  SisuPageSubHeader,
  SisuPageContent,
} from '../SisuPage';
import SisuForm from '../../SignupSignin/SisuForm';
import { errorDisplayed } from '../../../actions/login';
import { includePxJs } from '../../../helpers/px';

import styles from './ForgotPage.scss';

export interface ForgotPageProps {
  kind: 'password' | 'username';
  username?: string;
  usernameError?: string;
  emailSent?: boolean;
  generalError?: string;
  daProtect?: boolean;
  changeUsername(username: string): void;
}

const ForgotPage: FC<ForgotPageProps> = ({
  kind,
  username,
  usernameError,
  emailSent,
  generalError,
  daProtect,
  changeUsername,
}) => {
  const { t } = useTranslation();
  const dispatchErrorDisplayed = useAction(errorDisplayed);
  const isMobile = useContext(MobileContext);

  const form = useRef<HTMLFormElement | null>(null);
  const [hasEmailError, setEmailError] = useState(false);
  const [emailErrorDetails, setEmailErrorDetails] = useState('');

  useEffect(() => {
    includePxJs();
  }, []);

  // Log BI events for errors
  useEffect(() => {
    if (usernameError === 'social_account') {
      dispatchErrorDisplayed(
        'forgot.social_account',
        t(`forgot.error.social_account.${kind}`)
      );
    } else if (usernameError) {
      dispatchErrorDisplayed(
        `forgot.${usernameError}`,
        t(`forgot.error.${usernameError}`)
      );
    }
  }, [usernameError, kind, dispatchErrorDisplayed, t]);

  const oppositeKind = kind === 'password' ? 'username' : 'password';
  let usernameErrorDetails;
  if (usernameError === 'social_account') {
    usernameErrorDetails = (
      <>
        <div>{t(`forgot.error.social_account.${kind}`)}</div>
        <div>
          <a href="/users/login" className={styles['login-link']}>
            {t('forgot.link.login')}
          </a>
        </div>
      </>
    );
  } else if (usernameError) {
    usernameErrorDetails = t(`forgot.error.${usernameError}`);
  }

  const handleSubmit = e => {
    if (kind === 'username' && !isValidEmailAddress(username)) {
      e.preventDefault();
      setEmailError(true);
      setEmailErrorDetails(t(`forgot.error.invalid_email_address`));
      dispatchErrorDisplayed(
        'forgot.invalid_email_address',
        t(`forgot.error.invalid_email_address`)
      );
      return;
    }
    if (kind === 'password' && isValidEmailAddress(username)) {
      e.preventDefault();
      setEmailError(true);
      setEmailErrorDetails(t(`forgot.error.invalid_username`));
      dispatchErrorDisplayed(
        'forgot.invalid_username',
        t(`forgot.error.invalid_username`)
      );
      return;
    }
    setEmailError(false);
    form.current?.submit();
  };

  const forgotTheOtherLink = (
    <a
      className={styles['forgot-link']}
      href={`/users/forgot?kind=${oppositeKind}`}
      onClick={() => {
        setEmailErrorDetails('');
        setEmailError(false);
      }}
    >
      {t(`forgot.link.${oppositeKind}`)}
    </a>
  );

  return (
    <SisuPage
      className={c(styles['page'], daProtect && styles['protect'])}
      title={t(`forgot.page.${kind}.title`)}
      origin="/users/login"
      errorTextKey="login"
      errorType={generalError}
      withBackButton
    >
      {emailSent ? (
        <div className={styles['forgot-form-sent']}>
          <SisuPageHeader className={styles['header']}>
            {t('forgot.sent.title')}
          </SisuPageHeader>
          <SisuPageSubHeader className={styles['subheader']}>
            {t('forgot.sent.instruction')}
          </SisuPageSubHeader>
          <SisuPageContent className={styles['content']}>
            <a href="/users/login" className={styles['sent-back']}>
              <Button className={styles['button']} type="submit">
                {t('forgot.sent.back')}
              </Button>
            </a>
          </SisuPageContent>
        </div>
      ) : (
        <>
          <SisuPageHeader className={styles['header']}>
            {t(`forgot.${kind}.title`)}
          </SisuPageHeader>
          <SisuPageSubHeader className={styles['subheader']}>
            <Trans i18nKey={`forgot.${kind}.instruction`}>
              <br />
            </Trans>
          </SisuPageSubHeader>
          <SisuPageContent
            className={c(styles['content'], styles['input-updated'])}
          >
            <SisuForm
              action={`/_sisu/do/forgot?kind=${kind}`}
              method="post"
              className={styles['forgot-form']}
              onSubmit={handleSubmit}
              formRef={ref => (form.current = ref)}
            >
              <TextInput
                id="username"
                name="username"
                label={t(`forgot.form.${kind}.input`)}
                value={username || ''}
                validationResult={
                  hasEmailError || usernameError ? 'error' : undefined
                }
                details={usernameErrorDetails || emailErrorDetails}
                onChange={ev => changeUsername(ev.target.value)}
                className={styles['input']}
                autoFocus
                noBottomSpacing
              />

              <div className={styles['forgot-form-actions']}>
                {!isMobile && forgotTheOtherLink}
                <Button
                  className={styles['button']}
                  type="button"
                  onClick={handleSubmit}
                  disabled={!username || !!usernameError}
                >
                  {t('forgot.form.submit')}
                </Button>
                {isMobile && forgotTheOtherLink}
              </div>
            </SisuForm>
          </SisuPageContent>
        </>
      )}
    </SisuPage>
  );
};
ForgotPage.displayName = 'ForgotPage';

export default ForgotPage;
